<template>
  <div
    v-if="visible"
    class="border-primary-500 text-primary-500 inline-flex items-center gap-1 rounded border p-2 text-sm"
  >
    <TimerSvg />
    <span class="w-9">{{ timeLeft }}</span>
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, onBeforeUnmount, ref, watch } from 'vue';
import TimerSvg from 'images/front/icons/timer.svg';
import { useDialog } from 'primevue/usedialog';

const TimerExpiredDialog = defineAsyncComponent(() => import('./TimerExpiredDialog.vue'));

// Props
const props = defineProps({
  startAt: { type: [String, Number, Date], required: false, default: null },
  serverNow: { type: [String, Number, Date], required: false, default: null },
});

const ttlMinutes = 20;
const timeLeft = ref('');
let timerId = null;

const visible = computed(() => !!props.startAt);

const dialog = useDialog();
function openExpiredDialog() {
  dialog.open(TimerExpiredDialog, {
    props: {
      showHeader: false,
      dismissableMask: false,
      closable: false,
      modal: true,
    },
  });
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function toMs(val) {
  if (val == null) return NaN;
  const n = typeof val === 'number' ? val : new Date(val).getTime();
  // If seconds precision, convert to ms
  return n < 1e12 ? n * 1000 : n;
}

function computeExpiresMs() {
  const startMs = toMs(props.startAt);
  if (Number.isNaN(startMs)) return NaN;

  return startMs + ttlMinutes * 60 * 1000;
}

function computeNowMs() {
  const serverNowMs = toMs(props.serverNow);
  if (!Number.isNaN(serverNowMs)) return serverNowMs + (Date.now() - clientStartMs);
  return Date.now();
}

const clientStartMs = Date.now();

function update() {
  const expiresMs = computeExpiresMs();
  if (Number.isNaN(expiresMs)) {
    timeLeft.value = '';
    clearInterval(timerId);
    timerId = null;
    return;
  }

  const now = computeNowMs();
  const diff = Math.max(0, Math.floor((expiresMs - now) / 1000));
  const mm = Math.floor(diff / 60);
  const ss = diff % 60;
  timeLeft.value = `${pad(mm)}:${pad(ss)}`;

  if (diff <= 0) {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
    openExpiredDialog();
  }
}

watch(
  () => [props.startAt, props.serverNow],
  () => {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
    update();
    if (props.startAt) {
      timerId = setInterval(update, 1000);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
});
</script>
